import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";

import { TEAM_MEETINGS_DEFAULT_PARTICIPANT_LIMIT } from "@performyard/models";
import { toast } from "@performyard/py-components";

import type { EmployeeValue } from "../../../../components/Inputs/SelectEmployee";
import { createFetchEmployeesForPicker } from "../../../../components/Inputs/SelectEmployee/shared";
import WorkspaceFormModal from "./WorkspaceFormModal";

import { useCreateMeetingWorkspace, useCurrentUser } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

const EMPTY_PARTICIPANTS: EmployeeValue[] = [];

type CreateWorkspaceFormProps = {
	isOpen: boolean;
	handleClose: () => void;
};

const CreateWorkspaceForm = ({ isOpen, handleClose }: CreateWorkspaceFormProps) => {
	const queryClient = useQueryClient();
	const { data: currentUser } = useCurrentUser();
	const currentUserId = currentUser?.me?.authorization?.payload.user_id ?? "";

	// Feature flags
	const { value: isTeamMeetingsEnabled } = useFeatureFlagQuery("ff-is-team-meetings-enabled", false);
	const { value: participantLimit } = useFeatureFlagQuery(
		"ff-config-team-meetings-participant-limit",
		TEAM_MEETINGS_DEFAULT_PARTICIPANT_LIMIT
	);

	// Create workspace mutation
	const { mutate: createWorkspace, isPending } = useCreateMeetingWorkspace({
		onSuccess: () => {
			toast.success("Meeting Workspace Created");
			handleClose();
		},
	});

	// Fetch employees excluding current user
	const fetchEmployees = useCallback(
		async (searchTerm: string) => {
			const defaultFetch = createFetchEmployeesForPicker(queryClient);
			const employees = await defaultFetch(searchTerm);
			return employees.filter((emp) => emp.id !== currentUserId);
		},
		[queryClient, currentUserId]
	);

	const handleSubmit = (values: { name: string; participants: EmployeeValue[] }) => {
		if (!currentUserId) return;

		// Build participants array: current user (creator) + selected participants
		const participants = [
			{ role: "editor" as const, userId: currentUserId },
			...values.participants.map((p) => ({ role: "editor" as const, userId: p.id })),
		];

		createWorkspace({ name: values.name, participants });
	};

	return (
		<WorkspaceFormModal
			isOpen={isOpen}
			onClose={handleClose}
			onSubmit={handleSubmit}
			isPending={isPending}
			title="Create a New Meeting Workspace"
			submitLabel="Create Workspace"
			defaultName=""
			defaultParticipants={EMPTY_PARTICIPANTS}
			isTeamMeetingsEnabled={isTeamMeetingsEnabled}
			participantLimit={participantLimit}
			isParticipantEditable={true}
			fetchEmployees={fetchEmployees}
		/>
	);
};

export default CreateWorkspaceForm;
